import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  Settings,
  Globe,
  Mail,
  Phone,
  MapPin,
  Link as LinkIcon,
  Plus,
  Trash2,
  Save,
  Check,
  AlertCircle,
  AlertTriangle,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import '../styles/settings.css';

const SettingsPage = () => {
  const { token, admin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [settings, setSettings] = useState({
    site_name: 'VoxIT Media Consulting',
    tagline: 'Technology & Innovation for the Future',
    contact_info: {
      email: 'sales@voxitconsulting.media',
      phone: '+260 760930203',
      locations: ['Stockholm, Sweden', 'Lusaka, Zambia', 'Lahore, Pakistan']
    },
    social_links: [],
    footer_text: '',
    maintenance_mode: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/content/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/content/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSuccess('Settings saved successfully!');
      } else {
        setError('Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateContactInfo = (field, value) => {
    setSettings(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: value
      }
    }));
  };

  const addLocation = () => {
    setSettings(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        locations: [...prev.contact_info.locations, '']
      }
    }));
  };

  const updateLocation = (index, value) => {
    const locations = [...settings.contact_info.locations];
    locations[index] = value;
    updateContactInfo('locations', locations);
  };

  const removeLocation = (index) => {
    const locations = [...settings.contact_info.locations];
    locations.splice(index, 1);
    updateContactInfo('locations', locations);
  };

  const addSocialLink = () => {
    setSettings(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform: '', url: '', is_enabled: true }]
    }));
  };

  const updateSocialLink = (index, field, value) => {
    const links = [...settings.social_links];
    links[index] = { ...links[index], [field]: value };
    setSettings(prev => ({ ...prev, social_links: links }));
  };

  const removeSocialLink = (index) => {
    const links = [...settings.social_links];
    links.splice(index, 1);
    setSettings(prev => ({ ...prev, social_links: links }));
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        setSuccess('Password changed successfully!');
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
        setShowPasswordForm(false);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="settings-loading">
          <div className="loading-spinner" />
          <p>Loading settings...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="settings-page">
        <div className="settings-header">
          <div>
            <h2>Settings</h2>
            <p>Manage your website's global settings</p>
          </div>
          <button 
            className="btn-primary" 
            onClick={saveSettings}
            disabled={saving}
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {success && (
          <div className="message success">
            <Check size={18} /> {success}
          </div>
        )}
        {error && (
          <div className="message error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        <div className="settings-grid">
          {/* General Settings */}
          <div className="settings-card">
            <div className="card-header">
              <Globe size={20} />
              <h3>General</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label>Site Name</label>
                <input
                  type="text"
                  value={settings.site_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
                  placeholder="VoxIT Media Consulting"
                />
              </div>
              <div className="form-group">
                <label>Tagline</label>
                <input
                  type="text"
                  value={settings.tagline}
                  onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="Your site tagline"
                />
              </div>
              <div className="form-group">
                <label>Footer Text</label>
                <textarea
                  value={settings.footer_text}
                  onChange={(e) => setSettings(prev => ({ ...prev, footer_text: e.target.value }))}
                  placeholder="Custom footer message"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="settings-card">
            <div className="card-header">
              <Mail size={20} />
              <h3>Contact Information</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label><Mail size={14} /> Email</label>
                <input
                  type="email"
                  value={settings.contact_info.email}
                  onChange={(e) => updateContactInfo('email', e.target.value)}
                  placeholder="contact@example.com"
                />
              </div>
              <div className="form-group">
                <label><Phone size={14} /> Phone</label>
                <input
                  type="text"
                  value={settings.contact_info.phone}
                  onChange={(e) => updateContactInfo('phone', e.target.value)}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div className="form-group">
                <div className="label-with-action">
                  <label><MapPin size={14} /> Locations</label>
                  <button className="btn-add-small" onClick={addLocation}>
                    <Plus size={14} /> Add
                  </button>
                </div>
                <div className="locations-list">
                  {settings.contact_info.locations.map((loc, idx) => (
                    <div key={idx} className="location-item">
                      <input
                        type="text"
                        value={loc}
                        onChange={(e) => updateLocation(idx, e.target.value)}
                        placeholder="City, Country"
                      />
                      <button 
                        className="btn-remove-small" 
                        onClick={() => removeLocation(idx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="settings-card full-width">
            <div className="card-header">
              <LinkIcon size={20} />
              <h3>Social Links</h3>
              <button className="btn-add-small" onClick={addSocialLink}>
                <Plus size={14} /> Add Link
              </button>
            </div>
            <div className="card-content">
              {settings.social_links.length === 0 ? (
                <p className="empty-text">No social links added yet.</p>
              ) : (
                <div className="social-links-list">
                  {settings.social_links.map((link, idx) => (
                    <div key={idx} className="social-link-item">
                      <select
                        value={link.platform}
                        onChange={(e) => updateSocialLink(idx, 'platform', e.target.value)}
                      >
                        <option value="">Select Platform</option>
                        <option value="facebook">Facebook</option>
                        <option value="twitter">Twitter</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="instagram">Instagram</option>
                        <option value="youtube">YouTube</option>
                        <option value="tiktok">TikTok</option>
                      </select>
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateSocialLink(idx, 'url', e.target.value)}
                        placeholder="https://..."
                      />
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={link.is_enabled}
                          onChange={(e) => updateSocialLink(idx, 'is_enabled', e.target.checked)}
                        />
                        <span>Enabled</span>
                      </label>
                      <button 
                        className="btn-remove-small" 
                        onClick={() => removeSocialLink(idx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Maintenance Mode */}
          <div className="settings-card">
            <div className="card-header">
              <AlertTriangle size={20} />
              <h3>Maintenance Mode</h3>
            </div>
            <div className="card-content">
              <div className="form-group checkbox-group">
                <label className="checkbox-label warning">
                  <input
                    type="checkbox"
                    checked={settings.maintenance_mode}
                    onChange={(e) => setSettings(prev => ({ ...prev, maintenance_mode: e.target.checked }))}
                  />
                  <span>Enable Maintenance Mode</span>
                </label>
                <p className="help-text warning">When enabled, visitors will see a maintenance page instead of your site.</p>
              </div>
            </div>
          </div>

          {/* Admin Info */}
          <div className="settings-card">
            <div className="card-header">
              <Settings size={20} />
              <h3>Admin Account</h3>
            </div>
            <div className="card-content">
              <div className="admin-info">
                <div className="info-row">
                  <span>Name:</span>
                  <strong>{admin?.name}</strong>
                </div>
                <div className="info-row">
                  <span>Email:</span>
                  <strong>{admin?.email}</strong>
                </div>
              </div>
              
              {/* Change Password */}
              <div className="password-section">
                {!showPasswordForm ? (
                  <button 
                    className="btn-secondary"
                    onClick={() => setShowPasswordForm(true)}
                  >
                    <Lock size={16} /> Change Password
                  </button>
                ) : (
                  <div className="password-form">
                    <h4><Lock size={16} /> Change Password</h4>
                    <div className="form-group">
                      <label>Current Password</label>
                      <div className="password-input">
                        <input
                          type={showOldPassword ? 'text' : 'password'}
                          value={passwordData.old_password}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, old_password: e.target.value }))}
                          placeholder="Enter current password"
                        />
                        <button 
                          type="button"
                          className="toggle-visibility"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>New Password</label>
                      <div className="password-input">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                          placeholder="Enter new password"
                        />
                        <button 
                          type="button"
                          className="toggle-visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="password-actions">
                      <button 
                        className="btn-secondary"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn-primary"
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                      >
                        {changingPassword ? 'Changing...' : 'Change Password'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
